import React, { useEffect, useRef, useState } from 'react';

// Modal camera capture using getUserMedia. Works on both desktop
// (webcam) and mobile (rear/front camera) -- falls back to a clear
// error message if the browser/device has no camera or denies access.
const CameraCapture = ({ onCapture, onClose }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const [error, setError] = useState('');
  const [capturedBlob, setCapturedBlob] = useState(null);
  const [capturedPreview, setCapturedPreview] = useState(null);

  useEffect(() => {
    const startCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' }, // prefer rear camera on phones
        });
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          try {
            await videoRef.current.play();
          } catch (playErr) {
            // Some browsers reject play() if called too early; the
            // video will still start once metadata loads via autoPlay.
          }
        }
      } catch (err) {
        setError('Could not access your camera. Check your browser permissions, or upload a photo instead.');
      }
    };
    startCamera();

    return () => {
      streamRef.current?.getTracks().forEach((track) => track.stop());
    };
  }, []);

  const handleCapture = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);

    canvas.toBlob(
      (blob) => {
        setCapturedBlob(blob);
        setCapturedPreview(URL.createObjectURL(blob));
      },
      'image/jpeg',
      0.9
    );
  };

  const handleRetake = () => {
    setCapturedBlob(null);
    setCapturedPreview(null);
  };

  const handleUsePhoto = () => {
    const file = new File([capturedBlob], `recipe-scan-${Date.now()}.jpg`, { type: 'image/jpeg' });
    onCapture(file);
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h3 style={styles.title}>📷 Scan a Recipe</h3>
          <button onClick={onClose} style={styles.closeBtn}>✕</button>
        </div>

        {error ? (
          <p style={styles.error}>{error}</p>
        ) : capturedPreview ? (
          <>
            <img src={capturedPreview} alt="Captured recipe" style={styles.preview} />
            <div style={styles.actions}>
              <button onClick={handleRetake} style={styles.secondaryBtn}>Retake</button>
              <button onClick={handleUsePhoto} style={styles.primaryBtn}>Use This Photo</button>
            </div>
          </>
        ) : (
          <>
            <video ref={videoRef} autoPlay playsInline muted style={styles.video} />
            <div style={styles.actions}>
              <button onClick={onClose} style={styles.secondaryBtn}>Cancel</button>
              <button onClick={handleCapture} style={styles.primaryBtn}>📸 Capture</button>
            </div>
          </>
        )}

        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  },
  modal: {
    backgroundColor: 'white', borderRadius: '12px', padding: '20px', width: '420px', maxWidth: '90vw',
  },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' },
  title: { margin: 0, fontSize: '16px', color: '#333' },
  closeBtn: { background: 'none', border: 'none', fontSize: '16px', cursor: 'pointer', color: '#999' },
  video: { width: '100%', borderRadius: '8px', backgroundColor: '#000' },
  preview: { width: '100%', borderRadius: '8px' },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '14px' },
  primaryBtn: {
    padding: '10px 20px', backgroundColor: '#e67e22', color: 'white', border: 'none',
    borderRadius: '8px', fontWeight: '600', cursor: 'pointer',
  },
  secondaryBtn: {
    padding: '10px 20px', backgroundColor: '#f5f5f5', color: '#666', border: 'none',
    borderRadius: '8px', cursor: 'pointer',
  },
  error: { color: '#e74c3c', fontSize: '14px', textAlign: 'center', padding: '20px 0' },
};

export default CameraCapture;
